// Does this person belong to an accounting practice? (PRD 40 §5.2)
//
// THE GAP THIS CLOSES: Accountant HQ shipped complete and unreachable. Every
// HQ screen resolved its own firm by calling listFirms() on mount, the
// sidebar had no firm rows at all, and nothing in the login path looked at
// firms — so the only ways in were the activation screen (reached once, from
// an invitation) and typing the URL. A firm owner signing in the next morning
// landed on a personal dashboard with no route back. The same class of bug
// navReachability.test.ts was written for, one layer up: not a screen with no
// nav row, but a whole SECTION with no nav row.
//
// WHY A MODULE-LEVEL STORE AND NOT A CONTEXT: the answer is needed by
// WebNavigation, mounted in AppShell, and by LandingRedirect, AppShell's index
// child. Both render above every firm screen, and the question must not be
// asked twice. That is the same shape businessExperienceStore uses and for a
// related reason — with one difference: nobody else owns this fact, so this
// store does its own fetching instead of being published into.
//
// ONE BOOLEAN, NOT THE ROSTER. The nav and the landing redirect ask the same
// yes/no question. Caching the firm list here would be a second, staler copy
// of what the HQ screens already fetch for themselves, and a renamed practice
// could make it wrong; a membership existing or not cannot go stale in any
// way that matters to a menu.

import { firmService } from './firmService';

export interface FirmMembershipState {
  /**
   * Is practice workflow (PRD 61) switched on for this deployment? Delivered
   * on the same `GET /home` as `belongsToFirm`, because the sidebar needs both
   * before it draws and that request is already awaited on every page load.
   *
   * A ROLLOUT flag, never an entitlement: workflow is free (Dave, 2026-09-14),
   * so there is nothing to buy and nothing to revoke.
   *
   * ABSENT MEANS OFF, and there is no fallback fetch — unlike `belongsToFirm`,
   * whose absence means "ask separately". A flag the server has not mentioned
   * is one this deployment does not have, and there is no endpoint to ask. That
   * is also what lets this half merge before the API half: the rows simply do
   * not appear until the server starts sending it.
   */
  practiceWorkflow: boolean | null;
  /**
   * True when the caller holds at least one ACTIVE firm membership — which is
   * exactly what GET /firm/firms returns, so the question is the endpoint's
   * own and not re-derived here.
   *
   * Null means UNKNOWN: no mirrored answer, and no settled fetch.
   */
  belongsToFirm: boolean | null;
  /**
   * The role held in the FIRST firm — the same one `GET /firm/firms` returns
   * first and every HQ screen resolves to, so a practitioner in two practices
   * is asked about the one the app actually opens.
   *
   * A CONVENIENCE FOR THE MENU AND NEVER A CHECK. Every owner-only endpoint
   * calls `require_firm_role!` for itself, so a role that has gone stale here
   * costs a row that 403s on save, not access to anything. That is also why
   * this may sit in a store the comment above says holds no roster: a role is
   * one scalar the session already knows, not a cached list.
   *
   * Null means unknown OR no practice.
   */
  firmRole: string | null;
  /** True once a server answer has settled this session, success or failure. */
  hasLoaded: boolean;
}

export const FIRM_ROLE_OWNER = 'firm_owner';
/**
 * The other role that manages the practice rather than working in it. Together
 * with the owner it is the pair every `MANAGING_ROLES` gate on the api uses.
 */
export const FIRM_ROLE_ADMIN = 'firm_admin';

type Listener = (state: FirmMembershipState) => void;

const UNKNOWN: FirmMembershipState = {
  belongsToFirm: null,
  practiceWorkflow: null,
  firmRole: null,
  hasLoaded: false,
};

let state: FirmMembershipState = UNKNOWN;
let listeners = new Set<Listener>();
// Which user the in-flight (or settled) fetch belongs to, so signing in as
// somebody else re-asks rather than inheriting the last person's answer.
let loadedFor: string | null = null;

// THE MIRROR, and why it earns its keep here as it does for PRD 50: the
// sidebar and the landing redirect both render before any fetch can settle.
// Without a synchronously readable answer a practitioner would watch the
// Practice section pop in a beat late on every page load, and — because Dave
// chose to land firm members on HQ — would wait on a round trip at "/" every
// time instead of once per browser.
//
// Keyed by user uuid rather than cleared on sign-out, copying
// AppPreferencesContext exactly: the next person to use this browser cannot
// read the last person's answer even if a clear were missed.
const mirrorKey = (uuid: string | undefined): string | null =>
  uuid ? `gb_belongs_to_firm:${uuid}` : null;

const workflowMirrorKey = (uuid: string | undefined): string | null =>
  uuid ? `gb_practice_workflow:${uuid}` : null;

const roleMirrorKey = (uuid: string | undefined): string | null =>
  uuid ? `gb_firm_role:${uuid}` : null;

/** The mirrored role, or null when this browser has never been told. */
export function readRoleMirror(uuid: string | undefined): string | null {
  const key = roleMirrorKey(uuid);
  if (!key) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeRoleMirror(uuid: string | undefined, value: string | null): void {
  const key = roleMirrorKey(uuid);
  if (!key) return;
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    // Private mode, or storage full. The server still has the answer.
  }
}

/** The mirrored answer, or null when this browser has never been told. */
export function readFirmMirror(uuid: string | undefined): boolean | null {
  return readMirror(mirrorKey(uuid));
}

function writeFirmMirror(uuid: string | undefined, value: boolean): void {
  writeMirror(mirrorKey(uuid), value);
}

/** The mirrored rollout flag, or null when this browser has never been told. */
export function readWorkflowMirror(uuid: string | undefined): boolean | null {
  return readMirror(workflowMirrorKey(uuid));
}

function readMirror(key: string | null): boolean | null {
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? null : raw === 'true';
  } catch {
    // Private mode, or storage disabled. The fetch is still coming.
    return null;
  }
}

function writeMirror(key: string | null, value: boolean): void {
  if (!key) return;
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Private mode, or storage full. The server still has the answer.
  }
}

export function getFirmMembershipState(): FirmMembershipState {
  return state;
}

/** Subscribe to changes; returns the unsubscribe function. */
export function subscribeFirmMembership(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function publish(next: FirmMembershipState): void {
  state = next;
  listeners.forEach(listener => listener(state));
}

/** The settled answer for a known user: mirrored, and not asked again. */
function settle(
  uuid: string | undefined,
  belongsToFirm: boolean,
  practiceWorkflow = false,
  firmRole: string | null = null,
): void {
  writeFirmMirror(uuid, belongsToFirm);
  writeMirror(workflowMirrorKey(uuid), practiceWorkflow);
  writeRoleMirror(uuid, firmRole);
  loadedFor = uuid ?? null;
  publish({ belongsToFirm, practiceWorkflow, firmRole, hasLoaded: true });
}

/**
 * The answer as delivered by the session itself — `belongs_to_firm` on
 * `GET /home` and on the login payload (PRD 40 §5.6.1).
 *
 * THIS IS THE PATH THAT SHOULD RUN. The client already awaits /home on every
 * page load to validate its token and blocks rendering until it answers, so
 * the flag arrives before the sidebar or the landing redirect can ask for it:
 * no request, no wait, and the mirror demoted from load-bearing to a
 * belt-and-braces first frame.
 *
 * `ensureFirmMembershipLoaded` becomes a no-op for this user afterwards, which
 * is the whole point — the fetch below it exists only for the case where the
 * session did not carry the flag.
 */
export function settleFirmMembershipFromSession(
  uuid: string | undefined,
  belongsToFirm: boolean,
  practiceWorkflow?: boolean,
  firmRole?: string | null,
): void {
  settle(uuid, belongsToFirm, practiceWorkflow ?? false, firmRole ?? null);
}

/**
 * Something just changed the answer — today, the activation screen creating a
 * practice. Without this the accountant who was forced here by the §5.5.1 gate
 * reaches HQ with no Practice section until their next full page load, which
 * is the dead end this whole change exists to remove.
 *
 * TAKES NO UUID, deliberately. The obvious version read `user.uuid` off
 * AuthContext at the call site, and AccountantActivationPage is rendered in
 * Storybook with no AuthProvider — so the convenience of mirroring one frame
 * earlier would have broken two stories. It publishes in memory and then
 * forgets who it asked, so the next mount re-asks and writes the mirror with
 * the server's answer rather than this screen's guess.
 */
export function publishFirmMembership(belongsToFirm: boolean): void {
  loadedFor = null;
  // The rollout flag is the server's to state and this screen has not asked it,
  // so the previously published value is carried rather than guessed at.
  publish({
    belongsToFirm,
    practiceWorkflow: state.practiceWorkflow,
    // An accountant who just created a practice is its OWNER — that is what
    // `firms#create` does — so the Settings row appears in the same frame as
    // the rest of the section rather than after their next full page load.
    firmRole: belongsToFirm ? FIRM_ROLE_OWNER : null,
    hasLoaded: false,
  });
}

/**
 * Ask the server once per signed-in user. Safe to call on every render pass:
 * the second and later calls for the same user do nothing.
 *
 * THE FALLBACK, not the main path. When the session carried
 * `belongs_to_firm` this has already been settled and returns immediately;
 * this request is what happens when it did not — an API that predates the
 * flag, or a login path that stopped sending it.
 *
 * Keeping it is what makes the two repos independent: web is correct whether
 * or not the API half has shipped, so neither has to merge first.
 *
 * Seeds from the mirror first so a returning practitioner gets a correct FIRST
 * frame, then lets the server overwrite it — a membership revoked since the
 * last visit corrects itself on this request rather than persisting.
 */
export function ensureFirmMembershipLoaded(uuid: string | undefined): void {
  if (loadedFor === (uuid ?? null)) return;
  loadedFor = uuid ?? null;

  const mirrored = readFirmMirror(uuid);
  const mirroredWorkflow = readWorkflowMirror(uuid);
  const mirroredRole = readRoleMirror(uuid);
  if (mirrored !== null) {
    publish({
      belongsToFirm: mirrored,
      practiceWorkflow: mirroredWorkflow,
      firmRole: mirroredRole,
      hasLoaded: false,
    });
  }

  firmService
    .listFirms()
    .then(firms => {
      // `listFirms` cannot answer the rollout flag — only the session can — so
      // the mirrored value is carried rather than reset to false, which would
      // blink the Work rows out on any session that took this fallback path.
      // `listFirms` DOES know the role — it is on every row — and the first
      // row is the firm every HQ screen resolves to, so this fallback path
      // answers it properly rather than carrying a mirror forward.
      settle(uuid, firms.length > 0, mirroredWorkflow ?? false, firms[0]?.my_role ?? null);
    })
    .catch(() => {
      // A failed fetch keeps the mirrored answer rather than reverting to
      // "no practice": the last known answer is the better guess, and being
      // wrong here costs one menu section, not access to anything. Marked
      // loaded either way so the landing redirect cannot hang on a dead API.
      publish({
        belongsToFirm: state.belongsToFirm ?? false,
        practiceWorkflow: state.practiceWorkflow,
        firmRole: state.firmRole,
        hasLoaded: true,
      });
    });
}

/**
 * Back to unknown, WITHOUT touching any mirror — the uuid key is what keeps
 * each person's answer to themselves, so sign-out has nothing to erase.
 */
export function clearFirmMembership(): void {
  loadedFor = null;
  publish(UNKNOWN);
}

/** Test hook — drops listeners and state without notifying anybody. */
export function resetFirmMembershipStore(): void {
  state = UNKNOWN;
  listeners = new Set();
  loadedFor = null;
}
